<template>
  <div class="ModifyInsuranceModal">
    <v-form
      ref="$form"
      validate-on="input lazy"
      :key="loaded"
    >
      <BaseSelect
        id="people"
        :label="t('rater.common.for-whom.form-legend')"
        :value="newAnswers.forWhom"
        :items="forWhomOptions"
        v-model="newAnswers.forWhom"
      />

      <BaseSelect
        v-if="isModify"
        id="insurance-type"
        :label="t(`copy_life_insurance-type_form-title-${newAnswers.forWhom}`)"
        :value="newAnswers.insuranceType"
        :items="insuranceTypes"
        v-model="newAnswers.insuranceType"
      />

      <DatePickerComponent
        id="start-date"
        :label="t('rater.common.modify-search-view.start-date')"
        v-model="newAnswers.startDate"
        :datePickerVisible="!isModify"
        :min-date="minDateInit"
        :max-date="addDaysToDate(configurations.maxDays)"
        :rules="[required]"
      />

      <BirthDatesComponent
        :key="newAnswers.forWhom + ''"
        v-if="newAnswers.startDate"
        v-model="newAnswers.insureds"
        :for-whom="newAnswers.forWhom!"
        :configurations="configurations"
        :start-date="newAnswers.startDate!"
        :use-birth-date-actuarial="true"
      />

      <template v-if="mandatoryCoverageSlider.min && mandatoryCoverageSlider.max">
        <FormField
          class="mb-5"
          :statement="
            t('copy_life_modify-insurance_coverages-title', {
              min: addCommas(mandatoryCoverageSlider.min),
              max: addCommas(mandatoryCoverageSlider.max)
            })
          "
        >
          <AdvantageSliderComponent
            id="coverage-slider"
            v-model="coveragesCapital"
            :min="mandatoryCoverageSlider.min"
            :max="mandatoryCoverageSlider.max"
            :step="mandatoryCoverageSlider.step"
            :footer-message="
              t(`copy_common_slider-footer_min-max`, {
                min: addCommas(mandatoryCoverageSlider.min),
                max: addCommas(mandatoryCoverageSlider.max)
              })
            "
          />
        </FormField>

        <FormField
          v-if="showRevaluation"
          :statement="t('copy_life_modify-insurance_reevaluation-title')"
        >
          <BaseSelect
            id="reevaluation-type"
            class="reevaluation-type"
            v-model="reevaluationType"
            :value="reevaluationType"
            :items="reevaluationOptions"
          ></BaseSelect>
        </FormField>

        <p class="mb-4 text-black">
          {{ t('copy_life_modify-insurance_coverages-statement') }}
        </p>

        <CoverageComplete
          v-for="(coverage, i) in optionalCoverages"
          class="mb-4"
          :id="coverage.coverageCode"
          :key="coverage.coverageCode"
          :text="coverage.title"
          :tooltip="coverage.tooltip"
          :capitals="getSliderCapitals(coverage)"
          v-model:checked="optionalToSave[i].checked"
          v-model:capital="optionalToSave[i].value"
        />
      </template>

      <div class="actions">
        <BaseButton @click="submit">
          {{ t(`rater.base.${isModify ? 'recalculate-submit' : 'submit'}`) }}
        </BaseButton>

        <BaseButton
          v-if="isModify"
          type="transparent"
          @click="emit('close')"
        >
          {{ t('footer.exit') }}
        </BaseButton>
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
  import CoverageComplete from '@/modules/common/common/components/coverages/CoverageComplete.vue'
  import BirthDatesComponent from '@/modules/common/common/components/panels/BirthDatesComponent.vue'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import AdvantageSliderComponent from '@/modules/core/components/utils/AdvantageSliderComponent.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
  import { useCoverages } from '@/modules/life/common/composables/useCoveragesComposable'
  import LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import LifeRaterAnswers from '@/modules/life/rater/models/APP/LifeRaterAnswers'
  import { useLifeStore } from '@/modules/life/rater/stores/index'
  import { addDaysToDate } from '@/utils/date.utils'
  import { addCommas } from '@/utils/textUtils'
  import { computed, onBeforeMount, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const loadingStore = useLoadingStore()
  const sharedGtmDataStore = useSharedGtmDataStore()
  const lifeStore = useLifeStore()
  const errorManager = useErrorManagerComposable()
  const {
    mandatoryCoverageSlider,
    optionalCoverages,
    mandatoryToSave,
    optionalToSave,
    getSliderCapitals,
    loadCoverages
  } = useCoverages()
  const { required } = useRules()

  const emit = defineEmits(['submit', 'close'])

  interface PropsInterface {
    answers: LifeRaterAnswers
    configurations: LifeConfigurations
    isModify: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    isModify: true
  })

  const insuranceTypes = computed(() =>
    props.configurations.getInsuranceTypesOptions(
      newAnswers.value.forWhom ?? props.answers.forWhom!
    )
  )
  const forWhomOptions: OptionsValuesInterface[] = [
    {
      label: t('rater.common.for-whom.options.me'),
      value: ForWhomEnum.ME
    },
    {
      label: t('rater.common.for-whom.options.other'),
      value: ForWhomEnum.NOT_ME
    }
  ]

  const minDateInit = computed(() => {
    const tomorrow = addDaysToDate(1, new Date())
    tomorrow.setHours(0, 0, 0, 0)
    return tomorrow
  })

  const $form = ref()
  const newAnswers = ref(new LifeRaterAnswers(props.answers))
  const coveragesCapital = ref<number>(props.answers.coveragesCapital)
  const loaded = ref(0)

  const reevaluationOptions = computed(() =>
    props.configurations.reevaluationOptions.map((option) => ({
      label: option.description,
      value: option.value
    }))
  )

  const showRevaluation = computed(
    () => props.configurations.insuranceTypes.mortgage.id === newAnswers.value.insuranceType
  )

  const reevaluationType = ref<string | null>(
    props.answers.reevaluationType?.value ?? props.configurations.reevaluationOptions[0].value
  )

  watch(
    () => coveragesCapital.value,
    (capital: number | null) => {
      mandatoryToSave.value.forEach((c) => {
        c.value = capital
      })
      optionalToSave.value.forEach((c) => {
        const optionalCoverage = optionalCoverages.value.find((o) => o.coverageCode === c.key)

        if (!optionalCoverage?.coverageInfoCapitals.length) {
          c.value = capital
        }
      })
    }
  )

  watch(
    () => newAnswers.value.forWhom,
    (newForWhom: string | null) => {
      if (newForWhom) {
        if (newForWhom === ForWhomEnum.ME) {
          newAnswers.value.insureds[0].role = UserRoleEnum.HOLDER_INSURED
          newAnswers.value.insureds[0].riskNumber = 1
        } else {
          newAnswers.value.insureds[0].role = UserRoleEnum.INSURED
          newAnswers.value.insureds[0].riskNumber = 2
        }
        newAnswers.value.insureds[0].userDetails.birthDate = null
      }
    },
    { deep: true }
  )

  watch(
    () => newAnswers.value.insuranceType,
    async (newValue, oldValue) => {
      const hasChanged = newValue && oldValue && newValue !== oldValue
      if (!hasChanged) return

      const insuranceType = newAnswers.value.insuranceType

      // Reload coverages by insuranceType
      const storeCoverages = await lifeStore.loadCoverages({
        insuranceType: insuranceType!,
        preventCache: true
      })

      await loadCoverages(
        storeCoverages,
        props.answers.insureds[0],
        props.configurations.capitalOptions,
        coveragesCapital.value
      )
      newAnswers.value.insureds[0].additionalCoverages = []
      reevaluationType.value = null
    },
    { deep: true }
  )

  watch(
    () => newAnswers.value.insureds[0],
    async (insured) => {
      const insuranceType = newAnswers.value.insuranceType

      // Get stored coverages
      const storeCoverages = await lifeStore.loadCoverages({
        insuranceType: insuranceType!
      })

      await loadCoverages(
        storeCoverages,
        insured as Insured,
        props.configurations.capitalOptions,
        coveragesCapital.value
      )
    },
    { deep: true }
  )

  function initialize() {
    newAnswers.value.startDate = props.answers.startDate ?? addDaysToDate(1)
    newAnswers.value.insuranceType =
      props.answers.insuranceType ?? props.configurations.defaultInsuranceType
    newAnswers.value.insureds = props.answers.insureds
  }

  async function submit() {
    const { valid } = await $form.value.validate()
    if (!valid) return

    const coverages = mandatoryToSave.value.concat(optionalToSave.value)
    newAnswers.value.reevaluationType =
      props.configurations.reevaluationOptions.find(
        (option) => option.value === reevaluationType.value
      ) ?? null
    newAnswers.value.insureds[0].additionalCoverages = coverages

    sharedGtmDataStore.capital = coveragesCapital.value
    sharedGtmDataStore.additionalCoverages = optionalToSave.value.some(
      (coverage) => coverage.checked
    )
    emit('submit', newAnswers.value)
  }

  async function beforeMount() {
    try {
      loadingStore.startLoading()
      const insuranceType = newAnswers.value.insuranceType

      // Use to load coverages in calculator
      const clientCode = newAnswers.value.product?.clientCode
      const productCode = newAnswers.value.product?.productCode

      // Load coverages
      const storeCoverages = await lifeStore.loadCoverages({
        insuranceType: insuranceType!,
        clientCode: clientCode!,
        productCode: productCode!
      })
      const insured = props.answers.insureds[0]

      await loadCoverages(
        storeCoverages,
        insured,
        props.configurations.capitalOptions,
        coveragesCapital.value
      )

      initialize()
      loaded.value += 1
    } catch (error) {
      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .ModifyInsuranceModal {
    max-width: $size-350;
    margin: $spacing-l auto;
    width: $size-full;

    .tariff-subtitle {
      color: var(--pricing-subtitle-color);
      font-size: $font-size-l;
      text-transform: uppercase;
      margin-bottom: $spacing-xs;

      @include breakpoint-md {
        text-align: left;
      }
    }

    label {
      color: $color-neutral-dark;
    }

    .form-legend {
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      line-height: $line-height-42;
      margin-bottom: $spacing-xl;
      text-align: left;
      text-wrap: balance;
    }

    .relationship-type-legend {
      padding-bottom: $spacing-xl;
      display: block;
    }

    .actions {
      margin-top: $spacing-2;
      padding-bottom: $spacing-2xl;
    }

    .start-date_form-field {
      margin-top: $spacing-2;
    }
  }
</style>
